import React from 'react';
import { Empty, Skeleton } from 'antd';
import { CheckCircleOutlined, RightOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getComplianceOverviewQuery } from '@src/util/query/compliance.query';
import { getAppraisalOverviewQuery } from '@src/util/query/appraisal.query';
import { getSurveyOverviewQuery } from '@src/util/query/survey.query';
import { MetricTile, PanelCard } from './widgetShell';

// Each overview is one query; every widget reading it shares the cached answer.
const useCompliance = (skip) => {
  const { data, loading } = useQuery(getComplianceOverviewQuery, { skip, fetchPolicy: 'cache-and-network' });
  return { value: data?.getComplianceOverview?.response, loading };
};
const useAppraisals = (skip) => {
  const { data, loading } = useQuery(getAppraisalOverviewQuery, { skip, fetchPolicy: 'cache-and-network' });
  return { value: data?.getAppraisalOverview?.response, loading };
};
const useSurveys = (skip) => {
  const { data, loading } = useQuery(getSurveyOverviewQuery, { skip, fetchPolicy: 'cache-and-network' });
  return { value: data?.getSurveyOverview?.response, loading };
};

const pct = (n) => (n != null ? `${n}%` : '-');

// label, the figure, whether it's a warning, and where it goes
export const COMPLIANCE_STATS = {
  completion: { label: 'Compliance complete', value: (d) => `${d.totals?.completion ?? 0}%` },
  outstanding: { label: 'Documents outstanding', value: (d) => d.totals?.outstanding ?? 0 },
  overdue: { label: 'Overdue acknowledgements', value: (d) => d.totals?.overdue ?? 0, alert: (d) => d.totals?.overdue > 0 },
  reviewDue: { label: 'Documents due for review', value: (d) => d.totals?.reviewDue ?? 0, alert: (d) => d.totals?.reviewDue > 0 },
  expiring: { label: 'Expiring in 30 days', value: (d) => d.totals?.expiring ?? 0, alert: (d) => d.totals?.expiring > 0 },
  documents: { label: 'Live documents', value: (d) => d.totals?.documents ?? 0 },
};
export const APPRAISAL_STATS = {
  open: { label: 'Appraisals open', value: (d) => d.open ?? 0 },
  overdue: { label: 'Appraisals overdue', value: (d) => d.overdue ?? 0, alert: (d) => d.overdue > 0 },
  dueNext30Days: { label: 'Appraisals due in 30 days', value: (d) => d.dueNext30Days ?? 0 },
  unassigned: { label: 'Appraisals without a reviewer', value: (d) => d.unassigned ?? 0, alert: (d) => d.unassigned > 0 },
  completedThisYear: { label: 'Appraisals completed this year', value: (d) => d.completedThisYear ?? 0 },
  averageScore: { label: 'Average appraisal score', value: (d) => pct(d.averageScorePercent) },
};
export const SURVEY_STATS = {
  open: { label: 'Surveys open', value: (d) => d.open ?? 0 },
  awaiting: { label: 'Survey answers awaited', value: (d) => d.awaiting ?? 0 },
  responseRate: { label: 'Survey response rate', value: (d) => pct(d.responseRate) },
};

const SOURCES = {
  compliance: { stats: COMPLIANCE_STATS, use: useCompliance, to: '/user/compliance', hint: 'Open Compliance' },
  appraisals: { stats: APPRAISAL_STATS, use: useAppraisals, to: '/user/appraisals', hint: 'Open Appraisals' },
  surveys: { stats: SURVEY_STATS, use: useSurveys, to: '/user/surveys', hint: 'Open Surveys' },
};

/** One figure from the Compliance / Appraisals / Surveys overview; a click opens that module */
export const OverviewStatWidget = ({ source, stat }) => {
  const navigate = useNavigate();
  const def = SOURCES[source];
  const meta = def.stats[stat];
  const { value, loading } = def.use(false);
  return (
    <MetricTile
      label={meta.label}
      value={value ? meta.value(value) : undefined}
      loading={loading}
      alert={value ? !!meta.alert?.(value) : false}
      onClick={() => navigate(def.to)}
      hint={def.hint}
    />
  );
};

/** Sites with documents to sign, lowest completion first */
export const SignedBySiteWidget = () => {
  const navigate = useNavigate();
  const { value } = useCompliance(false);
  const sites = (value?.bySite || []).filter((row) => row.assignments > 0 && row.siteName).slice(0, 12);
  return (
    <PanelCard title="Signed by site" extra={<span className="dash-chart-note">lowest first</span>}>
      {!value ? <Skeleton active /> : sites.length ? (
        <div className="dash-site-list dw-scroll">
          {sites.map((row) => (
            <div key={row.siteId} className="dash-site-row" onClick={() => navigate('/user/compliance')}>
              <span className="dash-site-name" title={row.siteName}>{row.siteName || 'Site'}</span>
              <span className="dash-site-bar"><span style={{ width: `${row.completion}%` }} className={row.completion < 50 ? 'low' : row.completion < 100 ? 'mid' : 'full'} /></span>
              <span className="dash-site-figure">{row.completion}%</span>
              <span className="dash-site-sub">{row.acknowledged} of {row.assignments} signed{row.overdue ? <b> · {row.overdue} overdue</b> : ''}</span>
            </div>
          ))}
        </div>
      ) : <Empty description="No site documents yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </PanelCard>
  );
};

export const AppraisalStagesWidget = () => {
  const { value } = useAppraisals(false);
  const stages = value ? [
    { stage: 'Self-assessment', count: value.awaitingOfficer || 0 },
    { stage: 'With reviewer', count: value.awaitingReviewer || 0 },
    { stage: 'Officer to sign', count: value.awaitingSignOff || 0 },
  ] : [];
  return (
    <PanelCard title="Open appraisals by stage">
      {!value ? <Skeleton active /> : value.open > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stages} layout="vertical" margin={{ left: 20, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fill: '#8c8c8c', fontSize: 11 }} />
            <YAxis type="category" dataKey="stage" width={110} tick={{ fill: '#595959', fontSize: 12 }} />
            <Tooltip formatter={(v) => [v, 'Appraisals']} contentStyle={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }} />
            <Bar dataKey="count" fill="#17a2b8" radius={[0, 4, 4, 0]} barSize={22} />
          </BarChart>
        </ResponsiveContainer>
      ) : <div className="dw-empty"><Empty description="No open appraisals." image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>}
    </PanelCard>
  );
};

/** What needs doing across Compliance, Appraisals and Surveys (whichever this user has), most urgent first */
export const NeedsAttentionWidget = ({ access }) => {
  const navigate = useNavigate();
  const compliance = useCompliance(!access.compliance);
  const appraisalsQ = useAppraisals(!access.appraisals);
  const surveysQ = useSurveys(!access.surveys);
  const docs = compliance.value?.totals;
  const appraisals = appraisalsQ.value;
  const surveys = surveysQ.value;
  const attention = [
    docs?.overdue > 0 && { key: 'ackOverdue', text: `${docs.overdue} ${docs.overdue === 1 ? 'acknowledgement is' : 'acknowledgements are'} overdue`, to: '/user/compliance', urgent: true },
    appraisals?.overdue > 0 && { key: 'aprOverdue', text: `${appraisals.overdue} ${appraisals.overdue === 1 ? 'appraisal is' : 'appraisals are'} overdue`, to: '/user/appraisals', urgent: true },
    appraisals?.unassigned > 0 && { key: 'aprNoReviewer', text: `${appraisals.unassigned} ${appraisals.unassigned === 1 ? 'appraisal has' : 'appraisals have'} no reviewer`, to: '/user/appraisals', urgent: true },
    ...(compliance.value?.reviewDue || []).map((doc) => {
      const late = doc.reviewDate && dayjs(doc.reviewDate).isBefore(dayjs(), 'day');
      return { key: `review-${doc._id}`, text: `${late ? 'Review overdue' : 'Review due'}: ${doc.title}`, sub: [doc.appliesTo?.name, doc.reviewDate ? `review by ${dayjs(doc.reviewDate).format('DD/MM/YYYY')}` : null].filter(Boolean).join(' · '), to: '/user/compliance', urgent: late };
    }),
    docs?.expiring > 0 && { key: 'expiring', text: `${docs.expiring} ${docs.expiring === 1 ? 'document expires' : 'documents expire'} within 30 days`, to: '/user/compliance' },
    docs?.outstanding > 0 && { key: 'outstanding', text: `${docs.outstanding} ${docs.outstanding === 1 ? 'document is' : 'documents are'} still to be signed`, to: '/user/compliance' },
    appraisals?.dueNext30Days > 0 && { key: 'aprDue', text: `${appraisals.dueNext30Days} ${appraisals.dueNext30Days === 1 ? 'appraisal is' : 'appraisals are'} due in the next 30 days`, to: '/user/appraisals' },
    surveys?.awaiting > 0 && { key: 'svyAwaiting', text: `${surveys.awaiting} survey ${surveys.awaiting === 1 ? 'answer is' : 'answers are'} still awaited`, to: '/user/surveys' },
  ].filter(Boolean).sort((x, y) => (y.urgent ? 1 : 0) - (x.urgent ? 1 : 0));
  const loadingAny = (access.compliance && !compliance.value) || (access.appraisals && !appraisals) || (access.surveys && !surveys);

  return (
    <PanelCard title="Needs attention" className="dash-attention">
      {loadingAny ? <Skeleton active /> : attention.length ? (
        <div className="dash-attention-list dw-scroll">
          {attention.map((item) => (
            <div key={item.key} className={`dash-attention-item${item.urgent ? ' urgent' : ''}`} onClick={() => navigate(item.to)}>
              <span className="dash-attention-dot" />
              <span className="dash-attention-text">
                {item.text}
                {item.sub && <span className="dash-attention-sub">{item.sub}</span>}
              </span>
              <RightOutlined className="dash-attention-go" />
            </div>
          ))}
        </div>
      ) : (
        <div className="dash-attention-clear"><CheckCircleOutlined /> Nothing needs attention right now.</div>
      )}
    </PanelCard>
  );
};
